import {
  getOwnerDocument,
  getScrollParent,
  useGlobalListeners,
} from '@react-aria/utils';
import type { RefObject } from 'react';
import { useEffect } from 'react';

import { atScrollTop, hasScrollX } from '@infinex/ui-utils/dom';

/**
 * Mark nested interactive surfaces (e.g. dnd-kit drag handles) so sheet/tray
 * scroll-intervention and drag-to-dismiss do not claim the gesture.
 */
export const SHEET_GESTURE_EXEMPT_ATTR = 'data-sheet-gesture-exempt';

/**
 * Apply to nested drag handles inside a sheet/tray. Sets the exempt attribute
 * and stops Motion `drag="y"` from starting (capture-phase stopPropagation).
 */
export const sheetGestureExemptProps = {
  [SHEET_GESTURE_EXEMPT_ATTR]: true,
  onPointerDownCapture: (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  },
} as const;

/**
 * Enables native-feeling “drag to dismiss” behaviour on elements that contain
 * scrollable content.
 *
 * Normally, scrollable content will consume all vertical pan gestures,
 * interfering with the “drag to dismiss” interaction. This hook intervenes in
 * pointer and touch events so that scrolling and dragging never overlap.
 *
 * @param ref - A ref to the draggable container. Scrollable elements within are
 * detected automatically.
 */
export function useScrollIntervention(
  ref: RefObject<HTMLElement | null>,
  options: { allowScroll?: boolean } = {}
) {
  const { allowScroll = true } = options;

  const { addGlobalListener, removeAllGlobalListeners } = useGlobalListeners();

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    let didStartDrag = false;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let scrollTarget: Element | null = null;

    const ownerDocument = getOwnerDocument(container);

    const drag = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
        didStartDrag = true;
      }
    };
    const touchMove = (event: TouchEvent) => {
      if (!scrollTarget) return;
      if (!allowScroll) {
        drag(event);
        return;
      }

      const touchX = event.changedTouches[0].pageX;
      const touchY = event.changedTouches[0].pageY;
      const movementX = touchX - lastTouchX;
      const movementY = touchY - lastTouchY;
      lastTouchX = touchX;
      lastTouchY = touchY;

      // pan-x within a horizontally scrollable container, bail and allow scroll
      if (
        hasScrollX(scrollTarget) &&
        Math.abs(movementX) > Math.abs(movementY)
      ) {
        return;
      }

      // pan-up within a scroll container that's scrolled all the way to the
      // top, or has no scrollable content: prevent the default behavior and
      // allow drag to dismiss
      if (movementY > 0 && atScrollTop(scrollTarget)) {
        drag(event);
      }
    };

    const pointerMove = (event: PointerEvent) => {
      if (!scrollTarget) return;
      if (!allowScroll) return;

      // catch the event early, before `onDragStart` is called, to avoid both
      // scroll and drag being attempted at the same time:
      // drag starts, scroll starts, drag cancelled > awkward jerky motion
      if (!didStartDrag) {
        event.stopPropagation();
      }
    };

    const reset = () => {
      removeAllGlobalListeners();
      didStartDrag = false;
      scrollTarget = null;
    };

    const touchStart = (event: TouchEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(`[${SHEET_GESTURE_EXEMPT_ATTR}]`)
      ) {
        return;
      }

      lastTouchX = event.touches[0].pageX;
      lastTouchY = event.touches[0].pageY;
      scrollTarget = getScrollParent(event.target as Element, true);

      if (
        !scrollTarget ||
        // required guard because `getScrollParent` will keep looking for a
        // scrollable parent until it finds the root element
        !container.contains(scrollTarget)
      ) {
        return;
      }

      addGlobalListener(ownerDocument, 'pointermove', pointerMove);
      addGlobalListener(ownerDocument, 'touchcancel', reset);
      addGlobalListener(ownerDocument, 'touchend', reset);
      addGlobalListener(ownerDocument, 'touchmove', touchMove, {
        capture: true,
        passive: false,
      });
    };

    container.addEventListener('touchstart', touchStart, {
      capture: true,
      // since this listener adds "touchmove" it MUST be non-passive for
      // reliable `preventDefault`. especially on iOS, which assumes “this
      // gesture will never call preventDefault()” if the start event is passive
      passive: false,
    });

    return () => {
      container.removeEventListener('touchstart', touchStart, {
        capture: true,
      });

      reset();
    };
    // NOTE: ignore `ref` prop. we don't care about the stability of the _object_, just its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowScroll, addGlobalListener, removeAllGlobalListeners]);
}
